import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
    ChevronsLeft,
    ChevronsRight,
    LogOut,
    X
} from "lucide-react";

import { logout } from "../features/auth/authSlice";
import Header from "./Header";
import { MENU_GROUPS } from "./menuConfig";

const readCollapsed = () => {
    try {
        return localStorage.getItem("wms.sidebarCollapsed") === "1";
    } catch {
        return false;
    }
};

function SidebarNav({ collapsed, onNavigate }) {
    return (
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
            {MENU_GROUPS.map((group) => (
                <div key={group.title}>
                    {!collapsed ? (
                        <div className="px-3 mb-2 text-xs font-medium text-white/75">
                            {group.title}
                        </div>
                    ) : (
                        <div className="mx-3 mb-2 border-t border-white/20" />
                    )}

                    <ul className="space-y-0.5">
                        {group.items.map(({ to, label, icon: Icon }) => (
                            <li key={to}>
                                <NavLink
                                    to={to}
                                    onClick={onNavigate}
                                    title={collapsed ? label : undefined}
                                    className={({ isActive }) =>
                                        [
                                            "relative flex items-center gap-3 rounded-md text-sm transition-colors",
                                            collapsed ? "justify-center h-10" : "px-3 h-10",
                                            isActive
                                                ? "bg-white/20 text-white font-semibold"
                                                : "text-white/80 hover:bg-white/10 hover:text-white"
                                        ].join(" ")
                                    }
                                >
                                    {({ isActive }) => (
                                        <>
                                            {/* penanda aktif: strip kuning seperti label rak */}
                                            <span
                                                className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-r ${
                                                    isActive ? "bg-white" : "bg-transparent"
                                                }`}
                                            />
                                            <Icon size={18} strokeWidth={isActive ? 2.25 : 1.75} className="shrink-0" />
                                            {!collapsed && <span className="truncate">{label}</span>}
                                        </>
                                    )}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </nav>
    );
}

function UserBlock({ collapsed, onLogout }) {
    const user = useSelector((state) => state.auth?.user);
    const name = user?.name || user?.username || "User";
    const role = user?.role || "admin";

    return (
        <div className={`border-t border-white/20 p-3 ${collapsed ? "flex flex-col items-center gap-2" : ""}`}>
            <div className={`flex items-center gap-3 ${collapsed ? "" : "px-2 py-1"}`}>
                <div
                    className="w-9 h-9 rounded-full bg-white text-ink font-bold flex items-center justify-center shrink-0"
                    title={collapsed ? name : undefined}
                >
                    {name.charAt(0).toUpperCase()}
                </div>
                {!collapsed && (
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white truncate">{name}</div>
                        <div className="text-xs text-white/75 capitalize">{role}</div>
                    </div>
                )}
                {!collapsed && (
                    <button
                        type="button"
                        onClick={onLogout}
                        title="Keluar"
                        aria-label="Keluar"
                        className="p-2 rounded-md text-white/75 hover:text-white hover:bg-white/10"
                    >
                        <LogOut size={17} />
                    </button>
                )}
            </div>
            {collapsed && (
                <button
                    type="button"
                    onClick={onLogout}
                    title="Keluar"
                    aria-label="Keluar"
                    className="p-2 rounded-md text-white/75 hover:text-white hover:bg-white/10"
                >
                    <LogOut size={17} />
                </button>
            )}
        </div>
    );
}

function Brand({ collapsed }) {
    return (
        <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
            {/* logo: kotak kode rak */}
            <div className="w-9 h-9 rounded-[10px] bg-white flex items-center justify-center shrink-0 shadow-sm">
                <span className="text-ink font-bold text-sm leading-none">W</span>
            </div>
            {!collapsed && (
                <div className="leading-tight">
                    <div className="text-white font-bold tracking-tight">WMS Admin</div>
                    <div className="text-xs text-white/75">Warehouse Management</div>
                </div>
            )}
        </div>
    );
}

export default function AdminLayout() {
    const dispatch = useDispatch();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(readCollapsed);

    useEffect(() => {
        try {
            localStorage.setItem("wms.sidebarCollapsed", collapsed ? "1" : "0");
        } catch {
            /* abaikan */
        }
    }, [collapsed]);

    // tutup drawer mobile saat pindah halaman
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const handleLogout = () => dispatch(logout());

    // halaman dengan layout kartu sendiri; halaman lain tetap dibungkus panel putih
    const isDashboard = /\/(dashboard|inventory|location|analytics|palette|raking|report-stock|beginning-balance(?:\/\d+)?)\/?$/.test(location.pathname);

    return (
        <div className="flex min-h-screen bg-surface">

            {/* ===== Sidebar desktop ===== */}
            <aside
                className={`
                    ${collapsed ? "w-[72px]" : "w-64"}
                    hidden md:flex flex-col shrink-0
                    bg-gradient-to-b from-[#1AA3FA] to-[#0A5FE6] text-white
                    sticky top-0 h-screen
                    transition-[width] duration-200
                    no-print print:hidden
                `}
            >
                <div className={`h-16 flex items-center border-b border-white/20 ${collapsed ? "justify-center" : "px-5"}`}>
                    <Brand collapsed={collapsed} />
                </div>

                <SidebarNav collapsed={collapsed} />

                <button
                    type="button"
                    onClick={() => setCollapsed(!collapsed)}
                    className={`mx-3 mb-2 h-9 flex items-center gap-2 rounded-md text-xs text-white/75 hover:text-white hover:bg-white/5 ${
                        collapsed ? "justify-center" : "px-3"
                    }`}
                    aria-label={collapsed ? "Lebarkan sidebar" : "Ciutkan sidebar"}
                >
                    {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
                    {!collapsed && "Ciutkan menu"}
                </button>

                <UserBlock collapsed={collapsed} onLogout={handleLogout} />
            </aside>

            {/* ===== Drawer mobile ===== */}
            {mobileOpen && (
                <div className="fixed inset-0 z-50 flex md:hidden" role="dialog" aria-modal="true">
                    <div className="w-72 max-w-[85vw] bg-gradient-to-b from-[#1AA3FA] to-[#0A5FE6] text-white flex flex-col h-full">
                        <div className="h-16 flex items-center justify-between px-5 border-b border-white/20">
                            <Brand collapsed={false} />
                            <button
                                type="button"
                                onClick={() => setMobileOpen(false)}
                                aria-label="Tutup menu"
                                className="p-2 rounded-md text-white/75 hover:text-white hover:bg-white/10"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <SidebarNav collapsed={false} onNavigate={() => setMobileOpen(false)} />
                        <UserBlock collapsed={false} onLogout={handleLogout} />
                    </div>
                    <div
                        className="flex-1 bg-slate-900/40"
                        onClick={() => setMobileOpen(false)}
                    />
                </div>
            )}

            {/* ===== Konten ===== */}
            <div className="flex-1 min-w-0 flex flex-col">
                <Header onMenuClick={() => setMobileOpen(true)} />

                <main className="flex-1 p-4 md:p-6 print:p-0">
                    {isDashboard ? (
                        <Outlet />
                    ) : (
                        <div className="bg-white rounded-lg border border-line p-3 md:p-4 min-h-[calc(100vh-112px)] no-print print:border-0 print:p-0">
                            <Outlet />
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
